/**
 * Place地図のマーカーレイヤー
 *
 * 全Placeを訪問回数で色分けした丸マーカーで描画し、ズームアウト時はクラスタにまとめる。
 * マーカーは memo 化しているため、選択切替で再描画されるのは前後の 2 個だけになる。
 */

import { memo, useCallback } from 'react'

import type { Place } from '@/api/types/overland'
import { GoogleCircleMarker } from '@/components/shared/map/google-circle-marker'
import { useMarkerClusterer } from '@/components/shared/map/use-marker-clusterer'

/** このズーム以下ではクラスタ表示、超えると個別のマーカーを表示する */
export const PLACE_CLUSTER_MAX_ZOOM = 13

type RegisterMarker = (marker: google.maps.marker.AdvancedMarkerElement) => () => void

// 訪問回数に基づくマーカーサイズと色を計算
const getMarkerStyle = (visitCount: number) => {
  if (visitCount >= 50) return { radius: 12, color: '#EF4444', fillColor: '#EF4444' }
  if (visitCount >= 20) return { radius: 10, color: '#F97316', fillColor: '#F97316' }
  if (visitCount >= 10) return { radius: 8, color: '#EAB308', fillColor: '#EAB308' }
  if (visitCount >= 5) return { radius: 7, color: '#22C55E', fillColor: '#22C55E' }
  return { radius: 6, color: '#3B82F6', fillColor: '#3B82F6' }
}

type PlaceMarkerProps = {
  place: Place
  isSelected: boolean
  onSelect: (place: Place) => void
  register: RegisterMarker
}

const PlaceMarker = memo(function PlaceMarker({ place, isSelected, onSelect, register }: PlaceMarkerProps) {
  const style = getMarkerStyle(place.visit_count)

  // React 19 の ref クリーンアップでクラスタラーから外す。
  // vis.gl は position を後続の useEffect で入れるため、登録前に自前で入れておかないと
  // クラスタラーが位置なしのマーカーを索引してしまう。座標が変わったら登録し直して索引を更新する
  const { latitude, longitude } = place
  const markerRef = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement | null) => {
      if (!marker) return
      marker.position = { lat: latitude, lng: longitude }
      return register(marker)
    },
    [register, latitude, longitude],
  )

  return (
    <GoogleCircleMarker
      markerRef={markerRef}
      position={{ lat: place.latitude, lng: place.longitude }}
      radius={isSelected ? style.radius + 2 : style.radius}
      color={isSelected ? '#ffffff' : style.color}
      fillColor={style.fillColor}
      fillOpacity={0.8}
      weight={isSelected ? 3 : 2}
      zIndex={isSelected ? 1 : 0}
      onClick={() => onSelect(place)}
    />
  )
})

type PlaceMarkersLayerProps = {
  places: Place[]
  selectedPlaceId: number | null
  onSelectPlace: (place: Place) => void
}

export function PlaceMarkersLayer({ places, selectedPlaceId, onSelectPlace }: PlaceMarkersLayerProps) {
  const register = useMarkerClusterer({ maxZoom: PLACE_CLUSTER_MAX_ZOOM })

  return (
    <>
      {places.map((place) => (
        <PlaceMarker
          key={place.id}
          place={place}
          isSelected={place.id === selectedPlaceId}
          onSelect={onSelectPlace}
          register={register}
        />
      ))}
    </>
  )
}
